/**
 * Every procedural picture the game asks for by key, with the logical size of the file it
 * replaces. Keys:
 *   "tier/<assetId>"  merge creatures (EVOLUTION_CHAIN assetId), composite disc + creature
 *   "img/<key>"       gameplay / UI pictures (assets/game-image-urls.js keys)
 *
 * Sizes are the old PNG sizes: gameplay reads image aspect ratios (Fall spawn height,
 * Merge drop height, letterboxed flask, width/height:auto layouts), so they must not change.
 */
import { defineToonArt, getToonArtUrl } from "./art-registry.js";
import { drawCreature, drawCreatureShine } from "./creatures/index.js";
import { LINE_BODY } from "./palette.js";
import { drawBoulder, drawGem, drawShield, drawBuoy } from "./props/fall-props.js";
import { drawFlask, drawFlaskCrack1, drawFlaskCrack2, drawShard1, drawShard2 } from "./props/flask-art.js";
import {
  drawAdIcon, drawShakeIcon, drawClearIcon, drawShrinkIcon, drawDailyIcon, drawWheelIcon,
  drawChest, drawMinimizeAim,
} from "./icons/ui-icons.js";
import { drawPuzzlePiece1, drawPuzzlePiece2, drawPuzzlePiece3, drawPuzzlePiece4 } from "./props/puzzle-art.js";

/** Pixel sizes of the merge-tier PNGs the creatures replace (w, h). */
export var TIER_ART_SIZES = {
  cell: [260, 256],
  starfish: [256, 260],
  jellyfish: [262, 256],
  fish: [260, 256],
  snail: [256, 259],
  bug: [264, 256],
  butterfly: [262, 256],
  lizard: [264, 256],
  pterodactyl: [257, 256],
  trex: [259, 256],
  dragon_final: [256, 257],
};

/**
 * Disc radius for a creature canvas: the inscribed circle, shrunk so the ink outline
 * (LINE_BODY × r, centred on the edge) is not clipped.
 */
export function creatureDiscRadius(w, h) {
  return (Math.min(w, h) / 2) / (1 + LINE_BODY * 0.5 + 0.004);
}

export function tierArtKey(assetId) {
  return "tier/" + assetId;
}

/**
 * Layered creature art for in-world sprites, so a rolling body keeps its light upper left:
 *   "tierDisc/<id>"  the disc alone (no shine), drawn unrotated under the creature
 *   "tierBody/<id>"  the creature alone (eyes open), rotated with the body
 *   "tierBlink/<id>" the creature alone, eyes closed
 *   "tierShine/<id>" the disc's glint alone, drawn unrotated on top (the game draws one shared
 *                    glint, "tierShine/cell", on every tier: assets/merge-assets.js)
 * All share the "tier/<id>" frame, so they stack with identical layout. They are never encoded
 * for the DOM; assets/merge-assets.js bakes them at a scale chosen from the size they are
 * shown at (`bakeToonArtCanvas`), so draw sites size them from the logical frame (w × h).
 */
export function tierLayerKey(layer, assetId) {
  return layer + "/" + assetId;
}

var TIER_LAYERS = {
  tierDisc: { noCreature: true, noShine: true },
  tierBody: { noDisc: true },
  tierBlink: { noDisc: true, mood: "blink" },
  tierShine: null,
};

Object.keys(TIER_ART_SIZES).forEach(function (assetId) {
  var size = TIER_ART_SIZES[assetId];
  defineToonArt(tierArtKey(assetId), {
    w: size[0],
    h: size[1],
    name: assetId,
    draw: function (c, w, h) {
      c.translate(w / 2, h / 2);
      drawCreature(c, assetId, creatureDiscRadius(w, h), { t: 0 });
    },
  });
  // The same picture encoded at 2× for DOM images shown large (discovery flyer, victory
  // dragon); the regular URL is encoded at 1× to keep boot fast. DOM only: no canvas is kept.
  defineToonArt("tierLarge/" + assetId, {
    w: size[0],
    h: size[1],
    name: assetId,
    urlScale: 2,
    canvas: false,
    draw: function (c, w, h) {
      c.translate(w / 2, h / 2);
      drawCreature(c, assetId, creatureDiscRadius(w, h), { t: 0 });
    },
  });
  Object.keys(TIER_LAYERS).forEach(function (layer) {
    var pose = TIER_LAYERS[layer];
    defineToonArt(tierLayerKey(layer, assetId), {
      w: size[0],
      h: size[1],
      name: assetId + "-" + layer,
      draw: function (c, w, h) {
        c.translate(w / 2, h / 2);
        if (pose) drawCreature(c, assetId, creatureDiscRadius(w, h), pose);
        else drawCreatureShine(c, creatureDiscRadius(w, h));
      },
    });
  });
});

/** URL of a creature picture for large DOM display (encoded at 2×), or "" for unknown ids. */
export function largeTierImageUrl(assetId) {
  var id = String(assetId || "").trim();
  if (!id || !TIER_ART_SIZES[id]) return "";
  return getToonArtUrl("tierLarge/" + id);
}

/**
 * Where each picture is used decides what is drawn and kept (render/toon/art-registry.js):
 * canvas-only pictures are never PNG-encoded, DOM-only ones keep no canvas, and the gem (a
 * Fall pickup and a DOM icon) registers its 2× canvas so the Fall draws it sharp.
 * A DOM site must never use a CANVAS_ONLY key: its URL is a cache key, not a loadable image
 * (tests/toon-art-registry.test.js checks the DOM bindings).
 */
var CANVAS_ONLY = { dom: false };
var DOM_ONLY = { canvas: false };

/** key → [w, h, drawer, extra spec fields] */
var IMAGE_ART = {
  boulder: [256, 218, drawBoulder, CANVAS_ONLY],
  gem: [128, 113, drawGem, { cacheScale: 2 }],
  shield: [128, 141, drawShield, CANVAS_ONLY],
  flask: [256, 383, drawFlask, CANVAS_ONLY],
  flaskCrack: [256, 383, drawFlaskCrack1, CANVAS_ONLY],
  flaskCrack2: [256, 383, drawFlaskCrack2, CANVAS_ONLY],
  flaskShard1: [128, 128, drawShard1, CANVAS_ONLY],
  flaskShard2: [128, 128, drawShard2, CANVAS_ONLY],
  buoy: [300, 300, drawBuoy, CANVAS_ONLY],
  chest: [539, 440, drawChest, DOM_ONLY],
  minimizeAim: [32, 32, drawMinimizeAim, { scale: 1, url: "dataurl", canvas: false }],
  buttonAd: [270, 291, drawAdIcon, DOM_ONLY],
  buttonShake: [256, 256, drawShakeIcon, DOM_ONLY],
  buttonClear: [256, 256, drawClearIcon, DOM_ONLY],
  buttonShrink: [256, 256, drawShrinkIcon, DOM_ONLY],
  buttonDaily: [256, 256, drawDailyIcon, DOM_ONLY],
  buttonWheel: [256, 256, drawWheelIcon, DOM_ONLY],
  puzzlePiece1: [263, 330, drawPuzzlePiece1, { urlScale: 2, canvas: false }],
  puzzlePiece2: [330, 263, drawPuzzlePiece2, { urlScale: 2, canvas: false }],
  puzzlePiece3: [330, 263, drawPuzzlePiece3, { urlScale: 2, canvas: false }],
  puzzlePiece4: [263, 330, drawPuzzlePiece4, { urlScale: 2, canvas: false }],
};

/** Keys of IMAGE_ART drawn only on canvas (their URL is not loadable by the DOM). */
export var CANVAS_ONLY_IMAGE_KEYS = Object.keys(IMAGE_ART).filter(function (key) {
  return IMAGE_ART[key][3] === CANVAS_ONLY;
});

export var IMAGE_ART_KEYS = Object.keys(IMAGE_ART);

export function imageArtKey(key) {
  return "img/" + key;
}

IMAGE_ART_KEYS.forEach(function (key) {
  var row = IMAGE_ART[key];
  var extra = row[3] || {};
  defineToonArt(imageArtKey(key), {
    w: row[0],
    h: row[1],
    name: key,
    scale: extra.scale,
    urlScale: extra.urlScale,
    cacheScale: extra.cacheScale,
    dom: extra.dom,
    canvas: extra.canvas,
    url: extra.url,
    draw: row[2],
  });
});
